import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Link,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import clsx from 'clsx';
import { FolderExpand } from 'documents/documentAssignment/folder/list/FolderExpand';
import { useCommonStyles } from 'common/styles';
import { useStyles } from './index.styles';
import {
  TrainingFolders_trainingFolders,
  TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes_trainingFiles_nodes,
  TrainingFolders_trainingFolders_nodes__childFolders_edges_node_trainingFiles_nodes,
} from '../__generated__/TrainingFolders';
import { LookupTrainingSearch_lookupTrainingSearch_nodes } from '../helpSearchBar/__generated__/LookupTrainingSearch';


interface TreeNode {
  id: string;
  name: string;
  hint?: string | null;
  trainingFiles?: {
    nodes?: (
      | TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes_trainingFiles_nodes
      | TrainingFolders_trainingFolders_nodes__childFolders_edges_node_trainingFiles_nodes
      | LookupTrainingSearch_lookupTrainingSearch_nodes
    )[];
  } | null;
  _childFolders?: {
    edges?: { node: TreeNode }[];
    nodes?: TreeNode[];
  } | null;
}

type FolderNodeType = TreeNode;

const INDENT_SIZE = 50;

/* -------------------- HELPERS -------------------- */

function getChildNodes(node: TreeNode): TreeNode[] {
  if (!node._childFolders) return [];

  if (node._childFolders.edges) {
    return node._childFolders.edges.map((e) => e.node);
  }

  return node._childFolders.nodes ?? [];
}

function getParentPathIds(
  selectedId: string,
  nodes: TreeNode[]
): string[] {
  for (const node of nodes) {
    if (node.id === selectedId) {
      return [node.id];
    }

    const children = getChildNodes(node);
    const path = getParentPathIds(selectedId, children);

    if (path.length) {
      return [node.id, ...path];
    }
  }

  return [];
}

/* -------------------- NODE COMPONENT -------------------- */

interface FolderNodeProps {
  node: FolderNodeType;
  level?: number;
  openFolderIds: string[];
  onToggle: (node: FolderNodeType) => void;
  selectedFolderId: string | undefined;
}

const FolderNode: React.FC<FolderNodeProps> = ({
  node,
  level = 0,
  openFolderIds,
  onToggle,
  selectedFolderId,
}) => {
  const marginLeft = INDENT_SIZE * level;
  const commonStyles = useCommonStyles();
  const styles = useStyles();
  const theme = useTheme();

  const childNodes = getChildNodes(node);
  const isOpen = openFolderIds.includes(node.id);

  const textClass = clsx(
    node.id === selectedFolderId
      ? styles.selectedNodeStyle
      : level === 0
      ? commonStyles.semibold
      : styles.textStyle
  );

  return (
    <Stack
      style={{
        backgroundColor: isOpen ? theme.palette.neutralLighterAlt : undefined,
        paddingRight: 5,
      }}
    >
      <Link
        className={styles.linkHover}
        style={{ textDecoration: 'none' }}
        onClick={() => onToggle(node)}
      >
        <Stack
          verticalAlign="center"
          horizontal
          tokens={{ childrenGap: 20 }}
          style={{ marginLeft }}
        >
          <TooltipHost content={node.hint ?? undefined}>
            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
              {!!level ? (
                <FolderExpand isOpen={isOpen} />
              ) : (
                <IconButton
                  iconProps={{
                    iconName: isOpen ? 'ChevronDownMed' : 'ChevronLeftMed',
                  }}
                  ariaLabel="Expand"
                />
              )}

              <Text nowrap block className={textClass} variant="medium">
                {node?.name?.length && node.name.length > 30
                  ? node.name.slice(0, 27) + '…'
                  : node.name}
              </Text>
            </Stack>
          </TooltipHost>
        </Stack>
      </Link>

      {isOpen &&
        childNodes.map((child) => (
          <FolderNode
            key={child.id}
            node={child}
            level={level + 1}
            openFolderIds={openFolderIds}
            onToggle={onToggle}
            selectedFolderId={selectedFolderId}
          />
        ))}
    </Stack>
  );
};

/* -------------------- TREE COMPONENT -------------------- */

interface FolderTreeProps {
  data: TrainingFolders_trainingFolders;
  setTrainingFilesNodes: React.Dispatch<
    React.SetStateAction<
      | TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes_trainingFiles_nodes[]
      | TrainingFolders_trainingFolders_nodes__childFolders_edges_node_trainingFiles_nodes[]
      | LookupTrainingSearch_lookupTrainingSearch_nodes[]
    >
  >;
  setSelectedNodeName: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedFolderId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedFolderId: string | undefined;
}

const FolderTree: React.FC<FolderTreeProps> = ({
  data,
  setTrainingFilesNodes,
  setSelectedNodeName,
  setSelectedFolderId,
  selectedFolderId,
}) => {
  const styles = useStyles();
  const [openFolderIds, setOpenFolderIds] = useState<string[]>([]);

  /* ---- Auto open first node on first load ---- */
  useEffect(() => {
    if (!data?.nodes?.length) return;
    if (selectedFolderId) return;

    const firstNode = data.nodes[0] as TreeNode;

    setSelectedFolderId(firstNode.id);
    setSelectedNodeName(firstNode.name);
    setTrainingFilesNodes(firstNode.trainingFiles?.nodes ?? []);
    setOpenFolderIds([firstNode.id]);
  }, [data, selectedFolderId]);

 const handleToggle = (node: TreeNode) => {
  if (openFolderIds.includes(node.id) && selectedFolderId === node.id) {
    return;
  }

  setSelectedFolderId(node.id);
  setSelectedNodeName(node.name);
  setTrainingFilesNodes(node.trainingFiles?.nodes ?? []);

  const path = getParentPathIds(node.id, data.nodes as TreeNode[]); 
  setOpenFolderIds(path);
};

  return (
    <Stack grow className={styles.leftPanelContainer}>
      {data?.nodes?.map((folder) => (
        <FolderNode
          key={folder.id}
          node={folder as TreeNode}
          level={0}
          openFolderIds={openFolderIds}
          onToggle={handleToggle}
          selectedFolderId={selectedFolderId}
        />
      ))}
    </Stack>
  );
};

export default FolderTree;